<template>
  <client-only>
    <Popup
      class="product-options-popup"
      v-model:show="showing"
      width="full"
      transition="slide-up"
      ref="popup"
    >
      <div class="product-options-popup__body">
        <div class="product__selected" v-if="selectedVariant">
          <div class="product__image" v-if="selectedVariantImage">
            <sib-image loading="lazy" v-bind="selectedVariantImage"></sib-image>
          </div>
          <div class="product__content">
            <div class="product__title">
              {{ productStore.product?.title }}
            </div>
            <div class="product__price" v-if="discountApplied">
              <span class="product__price-final">
                {{ formatPrice(discountApplied.price) }}
              </span>
              <span
                class="product__price-compare"
                v-if="discountApplied.compareAtPrice"
              >
                {{ formatPrice(discountApplied.compareAtPrice) }}
              </span>
            </div>
          </div>
        </div>
        <ProductOptions />

        <ProductExtra />

        <div class="product__quantity">
          <h2>Quantity</h2>
          <Quantity
            base-class="product"
            :quantity="quantity"
            @update="updateQuantity"
          ></Quantity>
        </div>
      </div>
      <div class="product-options-popup__footer">
        <sib-button role="primary" @click="addToCart" :loading="loading">
          Add To Cart
        </sib-button>
      </div>
    </Popup>
  </client-only>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import ProductOptions from './ProductOptions.vue'
import EventBus from '@/services/eventbus'
import { computed, onMounted, onUnmounted, Ref, ref, watch } from 'vue'
import Popup from '@/ui-kits/Popup.vue'
import { formatPrice } from '@/utils/product'
import useDiscountStore from '@/store/discount'
import {
  EVENT_ADD_TO_CART,
  EVENT_UPDATE_QUANTITY,
  EVENT_SHOW_PRODUCT_OPTIONS_POPUP,
} from '@/utils'
import Quantity from '@/components/product/Quantity.vue'
import ProductExtra from './ProductExtra.vue'

const discountStore = useDiscountStore()

const productStore = useProductStore()
const showing = ref(false)
const popup: Ref<typeof Popup | null> = ref(null)
const loading = ref(false)
const quantity = ref(1)
const minQuantity = 1
const maxQuantity = 1000

const selectedVariant = computed(() => {
  return productStore.selectedVariant
})

const selectedVariantImage = computed(() => {
  return productStore.product?.images?.find(
    (item) => item.id == selectedVariant.value?.image?.id
  )
})

const discountApplied = computed(() => {
  if (!productStore.product) return null
  if (!selectedVariant.value) return null
  return discountStore.applyDiscount(
    productStore.product,
    selectedVariant.value.priceV2?.amount || 0,
    selectedVariant.value.compareAtPriceV2?.amount || 0,
    selectedVariant.value.sku
  )
})

onMounted(async () => {
  EventBus.on(EVENT_ADD_TO_CART, close)
  EventBus.on(EVENT_SHOW_PRODUCT_OPTIONS_POPUP, open)

  watch(
    () => quantity.value,
    () => {
      EventBus.trigger(EVENT_UPDATE_QUANTITY, +quantity.value)
    }
  )
})

onUnmounted(() => {
  EventBus.off(EVENT_SHOW_PRODUCT_OPTIONS_POPUP, open)
  EventBus.off(EVENT_ADD_TO_CART, close)
})

function addToCart() {
  const addToCartButton = document.querySelector(
    '.product__button-group .product__add-to-cart-button'
  ) as HTMLButtonElement

  if (!addToCartButton) return
  addToCartButton.click()
  loading.value = true
}

function updateQuantity(type: string) {
  if (type === 'minus') {
    quantity.value--
  } else if (type === 'plus') {
    quantity.value++
  }
  if (quantity.value < minQuantity) {
    quantity.value = minQuantity
  } else if (quantity.value > maxQuantity) {
    quantity.value = maxQuantity
  }
}

function open() {
  showing.value = true
}
function close() {
  showing.value = false
  loading.value = false
}
</script>

<style lang="scss">
.product-options-popup .popup__content {
  top: unset;
  bottom: 0;
  max-height: 550px;
  height: 70%;
  max-width: unset;
  padding: 0;
  .product-options-popup {
    &__body {
      height: calc(100% - 50px);
      overflow: auto;
      padding: 1rem 1rem 0 1rem;
    }
    &__footer {
      padding-right: 1rem;
      padding-left: 1rem;
    }
  }
  .product {
    &__selected {
      display: grid;
      grid-template-columns: 100px minmax(0, 1fr);
      grid-template-rows: max-content 1fr;
      gap: 0 1em;
      grid-template-areas: 'a b';
      margin-bottom: 2em;
    }
    &__image {
      grid-area: a;
      border: 1px solid var(--border-color);
    }
    &__content {
      grid-area: b;
      display: flex;
      flex-direction: column;
      gap: 0.75em;
    }
    &__title {
      font-weight: 600;
      font-size: 14px;
    }
    &__price {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      font-size: 14px;
    }
    &__price-final {
      color: var(--color-secondary);
      font-size: 14px;
    }
    &__price-compare {
      text-decoration: line-through;
      color: var(--text-color-tertiary);
      font-size: 14px;
    }
    &__quantity {
      margin-bottom: 1rem;
      h2 {
        font-size: 16px;
        font-weight: 600;
      }
    }
  }
  .lightbox-popup {
    height: 0;
  }
  .sib-button {
    max-width: unset;
  }
}
</style>
